const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

const getContractors = async (filters = {}) => {
  const { name, email, active, mobile, verification_status, page = 1, limit = 10 } = filters;
  
  const queryParams = new URLSearchParams();
  queryParams.append('page', String(page));
  queryParams.append('limit', String(limit));
  if (name) queryParams.append('name', name);
  if (email) queryParams.append('email', email);
  if (mobile) queryParams.append('mobile', mobile);
  if (active !== undefined && active !== '') {
    queryParams.append('active', active === 'true' ? true : false);
  }
  if (verification_status) queryParams.append('verification_status', verification_status);

  const response = await fetch(`${API_URL}/api/v1/admin/contractor?${queryParams.toString()}`, {
    headers: getAuthHeaders()
  });
  return response.json();
};

const getContractorById = async (contractorId) => {
  const response = await fetch(`${API_URL}/api/v1/admin/contractor/${contractorId}`, {
    headers: getAuthHeaders()
  });
  return response.json();
};

const createContractor = async (contractorData) => {
  // Convert commission_percentage and age to numbers
  const dataToSend = {
    ...contractorData,
    commission_percentage: Number(contractorData.commission_percentage),
    age: Number(contractorData.age)
  };
  
  const response = await fetch(`${API_URL}/api/v1/admin/contractor`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(dataToSend)
  });
  return response.json();
};

const updateContractor = async (contractorId, contractorData) => {
  // Convert active to boolean, commission_percentage and age to numbers
  const dataToSend = {
    ...contractorData,
    active: contractorData.active === 'true' ? true : false,
    commission_percentage: Number(contractorData.commission_percentage),
    age: Number(contractorData.age)
  };
  
  const response = await fetch(`${API_URL}/api/v1/admin/contractor/${contractorId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(dataToSend)
  });
  return response.json();
};

const getContractorWorkers = async (contractorId, page = 1, limit = 10) => {
  const response = await fetch(`${API_URL}/api/v1/admin/contractor/${contractorId}/workers?page=${page}&limit=${limit}`, {
    headers: getAuthHeaders()
  });
  return response.json();
};

const getContractorOrders = async (contractorId, status = null) => {
  const url = new URL(`${API_URL}/api/v1/admin/contractor/${contractorId}/orders`);
  if (status) url.searchParams.append('status', status);
  
  const response = await fetch(url, {
    headers: getAuthHeaders()
  });
  return response.json();
};

const getContractorServiceableJobs = async (contractorId, page = 1, limit = 20) => {
  const response = await fetch(
    `${API_URL}/api/v1/admin/contractor/${contractorId}/serviceable-jobs?disable_pagination=true`,
    {
      headers: getAuthHeaders()
    }
  );
  return response.json();
};

const addContractorServiceableJob = async (contractorId, jobData) => {
  const response = await fetch(`${API_URL}/api/v1/admin/contractor/${contractorId}/serviceable-jobs`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(jobData)
  });
  return response.json();
};

const addContractorServiceableJobsBulk = async (contractorId, jobs) => {
  const response = await fetch(`${API_URL}/api/v1/admin/contractor/${contractorId}/serviceable-jobs/bulk`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ jobs })
  });
  return response.json();
};

const updateContractorServiceableJob = async (contractorId, jobId, jobData) => {
  const response = await fetch(`${API_URL}/api/v1/admin/contractor/${contractorId}/serviceable-jobs/${jobId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(jobData)
  });
  return response.json();
};

const deleteContractorServiceableJob = async (contractorId, jobId) => {
  const response = await fetch(`${API_URL}/api/v1/admin/contractor/${contractorId}/serviceable-jobs/${jobId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  return response.json();
};

const getAdminServices = async () => {
  const response = await fetch(`${API_URL}/api/v1/admin/service?disable_pagination=true`, {
    headers: getAuthHeaders()
  });
  return response.json();
};

const getAdminCategories = async () => {
  const response = await fetch(`${API_URL}/api/v1/admin/categories?disable_pagination=true`, {
    headers: getAuthHeaders()
  });
  return response.json();
};

const getAdminSubCategories = async () => {
  const response = await fetch(`${API_URL}/api/v1/admin/subcategories?disable_pagination=true`, {
    headers: getAuthHeaders()
  });
  return response.json();
};

const getServiceableCities = async () => {
  const response = await fetch(`${API_URL}/api/v1/admin/serviceable-cities?disable_pagination=true`, {
    headers: getAuthHeaders()
  });
  return response.json();
};

const getContractorStats = async () => {
  const token = localStorage.getItem('token');

  const fetchCount = async (params = {}) => {
    const queryParams = new URLSearchParams({ page: '1', limit: '1', ...params });
    const response = await fetch(`${API_URL}/api/v1/admin/contractor?${queryParams.toString()}`, {
      headers: getAuthHeaders()
    });
    const data = await response.json();
    return data.pagination?.total ?? data.meta?.total ?? 0;
  };

  const fetchVerificationStats = async () => {
    const response = await fetch(`${API_URL}/api/v1/admin/verification/verification-stats`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.json();
  };

  const [total, inactive, verificationStats] = await Promise.all([
    fetchCount(),
    fetchCount({ active: 'false' }),
    fetchVerificationStats()
  ]);

  const contractorStats = verificationStats?.stats?.verifications?.CONTRACTOR || {};
  const verified = contractorStats.VERIFIED || 0;
  const pending = contractorStats.PENDING || 0;

  return { total, inactive, verified, pending };
};

export {
  getContractors,
  getContractorById,
  createContractor,
  updateContractor,
  getContractorWorkers,
  getContractorOrders,
  getContractorServiceableJobs,
  addContractorServiceableJob,
  addContractorServiceableJobsBulk,
  updateContractorServiceableJob,
  deleteContractorServiceableJob,
  getAdminServices,
  getAdminCategories,
  getAdminSubCategories,
  getServiceableCities,
  getContractorStats
}; 